/* CUSTOM_CONTENTLIBRARY_BY_CLAUDE (Ryan 2026-09-26) — Content Library, the portal page.
   Planner: Team-Portal-Bot/tools/Content-Library/CUSTOM-CONTENT-LIBRARY-PLANNER.md

   A quick lookup for the social agency (and reps / admins): find any LIVE product, see its key
   facts, open its product page or any collection it sits in, tick its photos and download them as
   one JPG ZIP.

   WHERE EACH THING COMES FROM
     - the product list, collections and chips: ONE call, GET /content-library-index (rfq-worker,
       rebuilt nightly). Search and filtering then run locally on it — instant.
     - a product's photos and description: /products/<handle>.js, RELATIVE (same origin as this
       page, public, always live — planner rule 9). Nothing here is ever staler than the website.
     - the ZIP: built in the browser by client-zip (vendored, assets/vendor-client-zip.js), from
       cdn.shopify.com, which sends `access-control-allow-origin: *` (rule 6).

   ⛔ EVERY DOWNLOAD IS A FULL-RES JPG: `format=pjpg` (rule 5). Originals are WEBP; `format=jpg`
      and `format=png` are ignored by the CDN.
   ⛔ EMPTY IS NOT FAILED (rule 7): "no products match", "the catalog has not been built" and "the
      catalog could not load" are three different screens.
   ⛔ NEVER A SHORT ZIP IN SILENCE (rule 6): a photo that fails to download is named on screen.

   The pure helpers are exposed on window.ContentLibrary._pure for tests/content-library-tests.mjs. */
(function () {
  'use strict';

  var INDEX_VERSION = 1;
  var STALE_HOURS = 48;
  var PAGE_SIZE = 60;
  var ZIP_LIB = (document.currentScript && document.currentScript.getAttribute('data-zip-lib')) || '';
  /* CUSTOM_CONTENTLIBRARY_ADS_BY_CLAUDE (Ryan 2026-10-21): the Create ads tab's engine and its fonts / logo, as the section
     names them (theme assets, so the worker that draws the JPGs loads the very same files). */
  var AD_MAKER = (document.currentScript && document.currentScript.getAttribute('data-ad-maker')) || '';
  var AD_ASSETS = (function (cs) {
    var a = function (n) { return (cs && cs.getAttribute(n)) || ''; };
    return { montserrat: a('data-ad-montserrat'), barlow600: a('data-ad-barlow600'), barlow700: a('data-ad-barlow700'), bsLogo: a('data-ad-bslogo'), bsLogoDark: a('data-ad-bslogo-dark'), wdPool: a('data-ad-wdpool') };
  })(document.currentScript);

  // ── Pure helpers ────────────────────────────────────────────────────────────────────────────

  function norm(s) {
    return String(s == null ? '' : s).toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ').trim();
  }

  /** Search text per product: title, vendor and handle, normalised once. */
  function searchKey(p) { return ' ' + norm(p.t + ' ' + p.v + ' ' + String(p.h).replace(/-/g, ' ')) + ' '; }

  /** Every word of the query must appear (AND); titles that contain the whole phrase rank first. */
  function searchProducts(products, keys, query) {
    var q = norm(query);
    if (!q) return products.slice();
    var words = q.split(' ');
    var hits = [];
    for (var i = 0; i < products.length; i++) {
      var k = keys[i];
      var ok = true;
      for (var w = 0; w < words.length; w++) { if (k.indexOf(words[w]) === -1) { ok = false; break; } }
      if (ok) hits.push(i);
    }
    var rank = function (i) {
      var t = ' ' + norm(products[i].t) + ' ';
      if (t.indexOf(' ' + q) === 1) return 0;          // title starts with the query
      if (t.indexOf(' ' + q) !== -1) return 1;          // a word in the title starts with it
      if (t.indexOf(q) !== -1) return 2;
      return 3;
    };
    hits.sort(function (a, b) { return rank(a) - rank(b) || a - b; });
    return hits.map(function (i) { return products[i]; });
  }

  /** Chips: one per group at most; groups combine as AND. `selected` = { groupTitle: collectionIndex }. */
  function filterByChips(products, selected) {
    var need = Object.keys(selected || {}).map(function (g) { return selected[g]; })
      .filter(function (i) { return typeof i === 'number'; });
    if (!need.length) return products;
    return products.filter(function (p) {
      for (var n = 0; n < need.length; n++) { if (p.c.indexOf(need[n]) === -1) return false; }
      return true;
    });
  }

  /** 'new' = most recently published first (index field `d`, epoch seconds; undated last); anything
   *  else keeps the order it was given (the index is already A-Z, a search already ranked). */
  function sortProducts(list, mode) {
    if (mode !== 'new') return list;
    return list.map(function (p, i) { return { p: p, i: i }; })
      .sort(function (a, b) { return ((b.p.d || 0) - (a.p.d || 0)) || (a.i - b.i); })
      .map(function (x) { return x.p; });
  }
  function hasDates(products) { return products.some(function (p) { return p && p.d; }); }
  function addedLabel(d) {
    if (!d) return '';
    return 'Added ' + new Date(d * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Denver' });
  }

  /** CDN URL with extra params, keeping whatever query string it already has. */
  function withParams(src, params) {
    var s = String(src || '');
    if (s.indexOf('//') === 0) s = 'https:' + s;
    var extra = Object.keys(params).map(function (k) { return k + '=' + encodeURIComponent(params[k]); }).join('&');
    return s + (s.indexOf('?') === -1 ? '?' : '&') + extra;
  }
  function thumbUrl(src, width) { return src ? withParams(src, { width: width || 400 }) : ''; }
  /** ⛔ `format=pjpg` — the only value the CDN honours; full resolution (rule 5). */
  function jpgUrl(src) { return withParams(src, { format: 'pjpg' }); }

  /** `<handle>-01.jpg`, numbered by the photo's position on the product page, not the tick order. */
  function zipFileName(handle, position, ext) {
    var n = position + 1;
    return handle + '-' + (n < 10 ? '0' + n : String(n)) + '.' + (ext || 'jpg');
  }

  /* CUSTOM_CONTENTLIBRARY_FORMATS_BY_CLAUDE (Ryan 2026-10-21): the download format. JPG stays the default (email and
     ad platforms take it; Google and Microsoft Ads refuse WEBP, Outlook will not show it). Measured on the CDN that day:
     `format=pjpg` gives a full-size JPG; asking with `Accept: image/webp` and no format gives the WEBP the site serves;
     nothing gives PNG, so PNG is made in the browser from the full-size photo. Any file that arrives in the wrong type
     (a few are stored as PNG and ignore pjpg) is converted too, so the ZIP always holds the format picked. */
  var FORMATS = {
    jpg: { label: 'JPG', mime: 'image/jpeg', ext: 'jpg', hint: 'JPG: best for email and ads (Meta, Google, Microsoft).' },
    png: { label: 'PNG', mime: 'image/png', ext: 'png', hint: 'PNG: for tools that ask for it. Larger files; a full-size photo can pass Google Ads\' 5 MB limit.' },
    webp: { label: 'WEBP', mime: 'image/webp', ext: 'webp', hint: 'WEBP: smallest, for websites. Not for email or Google / Microsoft ads.' },
  };
  function formatOf(key) { return FORMATS[key] ? key : 'jpg'; }
  /** What to ask the CDN for, per format: the URL and the Accept header. */
  function fetchPlan(src, key, keepAlpha) {
    var f = formatOf(key);
    // A logo's PNG starts from the WEBP so its transparent background survives (a JPG has none). Products keep the rule above.
    if (f === 'webp' || (keepAlpha && f === 'png')) return { url: withParams(src, {}).replace(/[?&]$/, ''), accept: 'image/webp' };
    return { url: jpgUrl(src), accept: 'image/jpeg,image/*;q=0.8' }; // PNG is made from the full-size JPG
  }
  function zipName(handle) { return handle + '-images.zip'; }

  function money(cents) {
    var n = Number(cents) / 100;
    if (!isFinite(n)) return '';
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function priceLabel(p, pv) {
    if (p == null) return '';
    var n = Number(p);
    var s = '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return pv ? 'From ' + s : s;
  }

  function ageText(builtAt, now) {
    var ms = (now || Date.now()) - Number(builtAt || 0);
    if (!(ms >= 0)) return { text: 'Catalog update time unknown', stale: true };
    var h = ms / 3600000;
    var text = h < 1 ? 'Catalog updated less than an hour ago'
      : h < 48 ? 'Catalog updated ' + Math.floor(h) + 'h ago'
      : 'Catalog last updated ' + Math.floor(h / 24) + ' days ago';
    return { text: text, stale: h > STALE_HOURS };
  }

  /** Rule 10: extracted, never generated. Intro = first non-empty <p>; bullets = the first five
   *  <li> of the first <ul>. `parse(html)` returns a Document (DOMParser in the page). */
  function keyFacts(descriptionHtml, parse) {
    var out = { intro: '', bullets: [] };
    if (!descriptionHtml) return out;
    var doc = parse(String(descriptionHtml));
    var ps = doc.querySelectorAll('p');
    for (var i = 0; i < ps.length; i++) {
      var t = (ps[i].textContent || '').replace(/\s+/g, ' ').trim();
      if (t) { out.intro = t; break; }
    }
    var ul = doc.querySelector('ul');
    if (ul) {
      var lis = ul.querySelectorAll('li');
      for (var j = 0; j < lis.length && out.bullets.length < 5; j++) {
        var b = (lis[j].textContent || '').replace(/\s+/g, ' ').trim();
        if (b) out.bullets.push(b);
      }
    }
    if (!out.intro && !out.bullets.length) {
      out.intro = (doc.body ? doc.body.textContent : '').replace(/\s+/g, ' ').trim().slice(0, 400);
    }
    return out;
  }

  /** Option lines for the card; Shopify's placeholder "Title: Default Title" says nothing. */
  function optionLines(options, max) {
    var lines = [];
    (options || []).forEach(function (o) {
      var vals = (o.values || []).filter(Boolean);
      if (!vals.length || (vals.length === 1 && vals[0] === 'Default Title')) return;
      var shown = vals.slice(0, max || 12);
      lines.push({ name: o.name, values: shown, more: vals.length - shown.length });
    });
    return lines;
  }

  /** Rule 9: image → tile, video / external_video → link, model → skipped. */
  function splitMedia(media) {
    var images = [], videos = [];
    (media || []).forEach(function (m) {
      if (!m) return;
      if (m.media_type === 'image' && m.src) images.push({ src: m.src, alt: m.alt || '', width: m.width, height: m.height });
      else if (m.media_type === 'external_video') {
        var url = m.host === 'youtube' ? 'https://www.youtube.com/watch?v=' + m.external_id
          : m.host === 'vimeo' ? 'https://vimeo.com/' + m.external_id : '';
        if (url) videos.push({ url: url, label: m.alt || (m.host === 'vimeo' ? 'Vimeo video' : 'YouTube video') });
      } else if (m.media_type === 'video') {
        var srcs = (m.sources || []).filter(function (s) { return s && s.url; });
        var best = srcs.filter(function (s) { return /mp4/.test(s.format || s.mime_type || ''); }).sort(function (a, b) { return (b.height || 0) - (a.height || 0); })[0] || srcs[0];
        if (best) videos.push({ url: withParams(best.url, {}).replace(/[?&]$/, ''), label: m.alt || 'Product video' });
      }
    });
    return { images: images, videos: videos };
  }

  /* The images BELOW the gallery on the live product page (Ryan 2026-10-21: "they all need to be" in the
     library). They come from the product's template: per-product feature/misc pictures bound by metafield,
     and the template's own shared photos. Reading the rendered page gets exactly what a shopper sees, so a
     disabled section or block is skipped by Shopify itself. Walked in page order; only the template's own
     sections (`template--…`), never the header/footer groups. Section types left out: the gallery (already
     tiles from the .js), small icons (Ryan: leave icons out), and other products' pictures (compare grid,
     recommendations). Video sections give their video as a link, never their poster frame. */
  var EXTRA_SKIP = /^(main|details|icons_with_text|logo_list|product[-_]comparison[-_]grid|product[-_]recommendations|recommendations|pop_up|apps)$/;
  var EXTRA_VIDEO = /^(background_video|video)$/;

  function sectionType(id) {
    var m = String(id || '').match(/^shopify-section-template--\d+__(.+)$/);
    return m ? m[1].replace(/_[A-Za-z0-9]{6}$/, '') : null;
  }
  /** The file's name, for spotting the same picture served from two URLs. */
  function fileKey(src) {
    var path = String(src || '').split('?')[0];
    return decodeURIComponent(path.slice(path.lastIndexOf('/') + 1)).toLowerCase();
  }
  /** A page <img> URL at full size: drop the display width/height/crop, keep the version. */
  function fullSize(src) {
    var s = String(src || '');
    var q = s.indexOf('?');
    if (q === -1) return s;
    var keep = s.slice(q + 1).split('&').filter(function (p) { return p && !/^(width|height|crop)=/.test(p); });
    return s.slice(0, q) + (keep.length ? '?' + keep.join('&') : '');
  }
  function pageExtras(doc, known) { return pageImages(doc, known, null); }

  /* CUSTOM_CONTENTLIBRARY_ADS_BY_CLAUDE (Ryan 2026-10-22): the words on a collection page a brand-wide ad may rest on:
     its sections' headings, paragraphs and list items (from the product-grid section only its description), never a
     product card, whose title carries one product's size. */
  function pageText(doc) {
    var out = [], seen = {};
    doc.querySelectorAll('[id^="shopify-section-template--"]').forEach(function (sec) {
      var skipped = COLLECTION_PAGE.skip.test(sectionType(sec.id) || '');
      sec.querySelectorAll(skipped ? '.rte p, .rte li' : 'h1, h2, h3, h4, p, li').forEach(function (n) {
        if (n.closest('a, form, script, style, noscript, [class*="product-card"], [class*="card-product"]')) return;
        var t = (n.textContent || '').replace(/\s+/g, ' ').trim();
        if (t.length < 12 || t.length > 300 || seen[t.toLowerCase()]) return;
        seen[t.toLowerCase()] = true;
        out.push(t);
      });
    });
    return out;
  }

  /* CUSTOM_CONTENTLIBRARY_TABS_BY_CLAUDE (Ryan 2026-10-21): the same walk for a blog article and a collection page,
     each with its own idea of which sections hold the pictures. `opts` null = the product page, exactly as before.
       only    a section type regex to take (the article: its `main` section, which holds the article image + body)
       skip    a section type regex to leave out (the collection: the product grid, compare grid, recently viewed, icons)
       srcRx   which image URLs count (an article body holds images pasted as cdn.shopify.com/s/files/... links)
       label   the words on a video link
       inlineVideos  also take YouTube / Vimeo / mp4 embedded inside a taken section (an article's body) */
  var EXTRA_SRC = /\/cdn\/shop\/(files|products)\//;
  var ARTICLE_PAGE = { only: /^main$/, srcRx: /\/cdn\/shop\/(files|articles)\/|cdn\.shopify\.com\/s\/files\//, label: 'Video in the article', inlineVideos: true };
  var COLLECTION_PAGE = {
    skip: /^(main|main[-_]collection|collection[-_]products|product[-_]grid|featured[-_]collection|product[-_]comparison[-_]grid|recently[-_]viewed|product[-_]recommendations|recommendations|icons_with_text|logo_list|pop_up|apps|custom_[a-z_]*why_blue_sky)$/,
    /* Ryan 2026-10-21: no trust badges. The "Why Blue Sky" sections hold them; a badge anywhere else is dropped by its file name. */
    dropRx: /trust[-_]?badge/i,
    srcRx: /\/cdn\/shop\/(files|collections)\/|cdn\.shopify\.com\/s\/files\//, label: 'Video on the collection page',
  };
  /* A facility page's section ids are named by hand (hero, products, trust_specs, cat_drill ...); sectionType cuts a
     6-letter tail, so vertical_schema reads "vertical". */
  var FACILITY_PAGE = {
    skip: /^(main|breadcrumbs[-_].*|trust[-_]cta|products|trust[-_]specs|faq|voice|form|eat|cat[-_]drill|vertical|vertical[-_]schema|recently[-_]viewed|custom_[a-z_]*why_blue_sky|pop_up|apps)$/,
    dropRx: /trust[-_]?badge/i,
    srcRx: /\/cdn\/shop\/files\/|cdn\.shopify\.com\/s\/files\//, label: 'Video on the page',
  };
  function pageImages(doc, known, opts) {
    var o = opts || {};
    var skip = o.skip || EXTRA_SKIP, only = o.only || null, srcRx = o.srcRx || EXTRA_SRC;
    var label = o.label || 'Video from the product page';
    var seen = {}, seenVideo = {}, images = [], videos = [];
    (known || []).forEach(function (m) { seen[fileKey(m.src)] = true; });
    var addVideo = function (url) {
      if (!url || !(/\.mp4/.test(url) || /youtube|vimeo/.test(url))) return;
      var clean = withParams(url, {}).replace(/[?&]$/, '');
      if (seenVideo[clean]) return;
      seenVideo[clean] = true;
      videos.push({ url: clean, label: label });
    };
    doc.querySelectorAll('[id^="shopify-section-template--"]').forEach(function (sec) {
      var type = sectionType(sec.id);
      if (!type || (only ? !only.test(type) : skip.test(type))) return;
      if (EXTRA_VIDEO.test(type)) {
        sec.querySelectorAll('video source[src], iframe[src]').forEach(function (v) { addVideo(v.getAttribute('src')); });
        return;
      }
      if (o.inlineVideos) sec.querySelectorAll('video source[src], iframe[src]').forEach(function (v) { addVideo(v.getAttribute('src')); });
      sec.querySelectorAll('img').forEach(function (img) {
        var src = img.getAttribute('src') || img.getAttribute('data-src') || '';
        if (!srcRx.test(src) || /\.svg(\?|$)/i.test(src) || (o.dropRx && o.dropRx.test(src))) return;
        var key = fileKey(src);
        if (seen[key]) return;
        seen[key] = true;
        images.push({ src: fullSize(src), alt: img.getAttribute('alt') || '' });
      });
    });
    return { images: images, videos: videos };
  }

  function parseHash(hash) {
    var m = String(hash || '').match(/(?:^#|&)p=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }
  function isBrandHash(hash) { return String(hash || '') === '#brand'; }

  /* CUSTOM_CONTENTLIBRARY_TABS_BY_CLAUDE (Ryan 2026-10-21): five tabs across the top. Product images opens on load (the
     page as it always was); Blog images, Collection images, Vendor logos and Brand files each have their own view.
     Everything is in the address, so a tab or an item can be linked and Back works:
       ''            Product images        #p=<handle>              one product
       #blog         Blog images           #a=<blog>/<article>      one article
       #banners      Collection images    #c=<handle>              one collection page
       #logos        Vendor logos
       #brand        Brand files
       #ads          Create ads           #ads=p:<handle> | v:<vendor> | c:<handle> | w:weight-designer   (CUSTOM_CONTENTLIBRARY_ADS_BY_CLAUDE) */
  var TABS = [
    { id: 'products', label: 'Product images', hash: '#' },
    { id: 'blog', label: 'Blog images', hash: '#blog' },
    { id: 'banners', label: 'Collection images', hash: '#banners' },
    { id: 'logos', label: 'Vendor logos', hash: '#logos' },
    { id: 'brand', label: 'Brand files', hash: '#brand' },
    { id: 'ads', label: 'Create ads', hash: '#ads' },
  ];
  /** The address -> { tab, item }. `item` is the product handle / "blog/article" / collection handle, else null. */
  function parseRoute(hash) {
    var h = String(hash || '');
    var key = function (name) {
      var m = h.match(new RegExp('(?:^#|&)' + name + '=([^&]+)'));
      if (!m) return null;
      try { return decodeURIComponent(m[1]); } catch (e) { return m[1]; }
    };
    // the ad maker first: its item ("p:<handle>") must never be read as a product address
    var ad = key('ads'); if (ad && /^[pvcw]:.+/.test(ad)) return { tab: 'ads', item: ad };   // w: the Weight Designer (Phase 5)
    if (h === '#ads') return { tab: 'ads', item: null };
    var p = key('p'); if (p) return { tab: 'products', item: p };
    var a = key('a'); if (a && /^[^/]+\/[^/]+$/.test(a)) return { tab: 'blog', item: a };
    var c = key('c'); if (c) return { tab: 'banners', item: c };
    var pg = key('pg'); if (pg) return { tab: 'banners', item: 'pg:' + pg }; // a facility page (CUSTOM_CONTENTLIBRARY_FACILITYPAGES_BY_CLAUDE)
    if (h === '#blog') return { tab: 'blog', item: null };
    if (h === '#banners') return { tab: 'banners', item: null };
    if (h === '#logos') return { tab: 'logos', item: null };
    if (h === '#brand') return { tab: 'brand', item: null };
    return { tab: 'products', item: null };
  }

  /* The blog list comes from the site's own article sitemap (/sitemap.xml -> sitemap_articles_N.xml): public, same
     origin, every LIVE article with its title and main image, one small file. Read with plain string work so it runs
     anywhere (no XML parser needed for a file this regular). */
  function xmlText(s) {
    return String(s == null ? '' : s).replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, '$1')
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&#39;/g, "'")
      .replace(/&#(\d+);/g, function (m, n) { return String.fromCharCode(Number(n)); }).replace(/&amp;/g, '&').trim();
  }
  function xmlTag(block, tag) {
    var m = String(block).match(new RegExp('<' + tag + '[^>]*>([\\s\\S]*?)</' + tag + '>'));
    return m ? xmlText(m[1]) : '';
  }
  /** The article sitemaps a sitemap index lists, as same-origin paths. */
  function articleSitemapPaths(indexXml) {
    var out = [];
    String(indexXml || '').replace(/<loc>([\s\S]*?)<\/loc>/g, function (m, loc) {
      var u = xmlText(loc);
      if (/sitemap_articles_\d+\.xml/.test(u)) out.push(u.replace(/^https?:\/\/[^/]+/, ''));
      return m;
    });
    return out;
  }
  /** One article sitemap -> [{ blog, handle, path, title, img, lastmod }], in the file's order. */
  function parseArticleSitemap(xml) {
    var out = [];
    String(xml || '').split(/<url>/).slice(1).forEach(function (block) {
      var loc = xmlTag(block, 'loc');
      var m = loc.match(/\/blogs\/([^/?#]+)\/([^/?#]+)/);
      if (!m) return;
      var title = xmlTag(block, 'image:title') || m[2].replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
      out.push({ blog: m[1], handle: m[2], path: '/blogs/' + m[1] + '/' + m[2], title: title,
        img: xmlTag(block, 'image:loc') || null, lastmod: xmlTag(block, 'lastmod') || null });
    });
    return out;
  }
  /** "flooring-guide" -> "Flooring Guide". */
  function blogLabel(handle) { return String(handle || '').replace(/[-_]+/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); }); }
  /** Newest updated first; an undated or unreadable date last; ties keep the sitemap's order. */
  function sortArticles(list) {
    return list.map(function (a, i) { return { a: a, i: i, t: Date.parse(a.lastmod || '') || 0 }; })
      .sort(function (x, y) { return (y.t - x.t) || (x.i - y.i); }).map(function (x) { return x.a; });
  }
  /** Every word must appear in the title (or the blog's name). */
  function searchTitles(list, query, textOf) {
    var q = norm(query);
    if (!q) return list.slice();
    var words = q.split(' ');
    return list.filter(function (x) {
      var t = ' ' + norm(textOf(x)) + ' ';
      for (var i = 0; i < words.length; i++) { if (t.indexOf(words[i]) === -1) return false; }
      return true;
    });
  }
  /** The file name inside the logo ZIP: "Legend Fitness" -> "Legend-Fitness-logo". */
  function logoFileBase(name) {
    var s = String(name || '').normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/&/g, ' and ')
      .replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    return (s || 'logo') + '-logo';
  }
  /* END CUSTOM_CONTENTLIBRARY_TABS_BY_CLAUDE */

  /* CUSTOM_CONTENTLIBRARY_BRAND_BY_CLAUDE (Ryan 2026-10-21). BRAND = a fixed image in the product's page template
     (the index lists each template's images), so every product on that design shows it. PRODUCT = anything else on
     the page: its own gallery and its own per-product pictures. null when the index has no template list (an older
     index, or the theme scope missing): then no tile is tagged rather than every tile guessed. */
  function brandSetFor(index, entry) {
    var t = index && index.templates;
    if (!t) return null;
    var list = (entry && entry.tp && t['product.' + entry.tp]) || t.product || [];
    var set = {};
    list.forEach(function (f) { set[String(f).toLowerCase()] = true; });
    return set;
  }
  function photoTag(src, set) { return set ? (set[fileKey(src)] ? 'brand' : 'product') : null; }

  /** "BSFS-Brand-2-Primary-Logo.png" downloads as "BSFS-Primary-Logo.png". */
  function brandDownloadName(f) { return String(f || '').replace(/^BSFS-Brand-(\d+-)?/i, 'BSFS-'); }
  function fileSizeLabel(bytes) {
    var n = Number(bytes);
    if (!(n > 0)) return '';
    return n < 1024 * 1024 ? Math.max(1, Math.round(n / 1024)) + ' KB' : (n / 1048576).toFixed(1) + ' MB';
  }
  function fileTypeLabel(f, mt) {
    var ext = String(f || '').split('.').pop().toUpperCase();
    return ext && ext.length <= 4 ? ext : String(mt || '').split('/').pop().toUpperCase();
  }

  // ── DOM helpers ─────────────────────────────────────────────────────────────────────────────

  function el(tag, attrs, kids) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      var v = attrs[k];
      if (v == null || v === false) return;
      if (k === 'class') n.className = v;
      else if (k === 'text') n.textContent = v;
      else if (k.indexOf('on') === 0) n.addEventListener(k.slice(2), v);
      else n.setAttribute(k, v === true ? '' : v);
    });
    (kids || []).forEach(function (c) { if (c != null) n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return n;
  }
  function clear(n) { while (n.firstChild) n.removeChild(n.firstChild); }

  // ── App ─────────────────────────────────────────────────────────────────────────────────────

  /* The sort choice is a per-viewer convenience, remembered in this browser only. */
  /* Each kind of set remembers its own format: photos default to JPG, logos to PNG (a logo needs its see-through
     background, which a JPG cannot hold). */
  function readFormat(kind) {
    var fallback = kind === 'logos' ? 'png' : 'jpg';
    try { var v = localStorage.getItem(kind === 'logos' ? 'ccl-format-logos' : 'ccl-format'); return v ? formatOf(v) : fallback; }
    catch (e) { return fallback; }
  }
  function writeFormat(v, kind) { try { localStorage.setItem(kind === 'logos' ? 'ccl-format-logos' : 'ccl-format', v); } catch (e) { /* private mode */ } }
  function readSort() { try { return localStorage.getItem('ccl-sort') === 'new' ? 'new' : 'az'; } catch (e) { return 'az'; } }
  function writeSort(v) { try { localStorage.setItem('ccl-sort', v); } catch (e) { /* private mode */ } }

  var state = {
    index: null, keys: [], byHandle: {},
    query: '', group: null, chips: {}, shown: PAGE_SIZE, sort: readSort(),
    product: null, selected: {}, busy: false, format: readFormat(),
    // CUSTOM_CONTENTLIBRARY_TABS_BY_CLAUDE: the set on screen (any tab) and each tab's own browse state.
    set: null, tab: null,
    blog: { list: null, loading: null, error: null, query: '', filter: '', shown: PAGE_SIZE },
    banners: { query: '', shown: PAGE_SIZE },
  };
  var root, tabsEl, browseEl, panelEl, productEl, observer;

  function api() { return window.RepPortal && window.RepPortal.API; }

  /* CUSTOM_CONTENTLIBRARY_ADS_BY_CLAUDE (Ryan 2026-10-22): who sees the Create ads tab, set per person in Settings
     (Module Visibility, "Content Library · Create ads"). The worker refuses the ad routes to anyone else; this only
     decides whether the tab is drawn. Admin: always. External user (role agency): `contentlibraryads` in the token's
     mods. Team member: unless their page config (the drawer's cached effectivePages) says hidden. */
  function adsAllowed(sess, pages) {
    if (!sess) return false;
    if (sess.admin) return true;
    if (sess.role === 'agency') {
      try {
        var part = String(sess.accessToken || '').split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        while (part.length % 4) part += '=';
        var mods = JSON.parse(atob(part)).mods;
        return Array.isArray(mods) && mods.indexOf('contentlibraryads') !== -1;
      } catch (e) { return false; }
    }
    var v = pages ? pages.contentlibraryads : undefined;
    return !(v === false || v === 'hidden' || v === 'disabled');
  }
  function canAds() {
    var sess = null, pages = null;
    try { sess = JSON.parse(sessionStorage.getItem('rep_portal_session') || 'null'); } catch (e) { sess = null; }
    try { pages = JSON.parse(sessionStorage.getItem('rep_portal_page_config') || 'null'); } catch (e) { pages = null; }
    return adsAllowed(sess, pages);
  }

  function notice(kind, title, lead, extra) {
    /* Ryan 2026-10-22: loading looks like Marketing Ops' (rep-paid-ads.liquid .moph-updating): a spinning ring and the
       words in light blue, centred, no box */
    if (kind === 'loading') {
      return el('div', { class: 'ccl-loading', role: 'status', 'aria-live': 'polite' }, [el('span', { class: 'ccl-loading__spinner', 'aria-hidden': 'true' }), el('span', { text: title })]);
    }
    return el('div', { class: 'ccl-notice ccl-notice--' + kind, role: kind === 'error' ? 'alert' : null }, [
      el('p', { class: 'ccl-notice__title', text: title }),
      lead ? el('p', { class: 'ccl-notice__lead', text: lead }) : null,
      extra || null,
    ]);
  }

  function loadIndex() {
    clear(root);
    root.appendChild(notice('loading', 'Loading the catalog…'));
    api().get('/content-library-index').then(function (idx) {
      if (!idx || idx.v !== INDEX_VERSION || !Array.isArray(idx.products)) {
        clear(root);
        root.appendChild(notice('error', 'This page needs an update',
          'The catalog arrived in a format this page does not understand (version ' + (idx && idx.v) + '). Reload the page; if it persists, tell Ryan.'));
        return;
      }
      state.index = idx;
      state.keys = idx.products.map(searchKey);
      idx.products.forEach(function (p) { state.byHandle[p.h] = p; });
      renderShell();
      route();
    }).catch(function (err) {
      clear(root);
      var retry = el('button', { class: 'ccl-btn', type: 'button', text: 'Try again', onclick: loadIndex });
      if (err && err.status === 503 && err.data && err.data.error === 'not-built') {
        root.appendChild(notice('error', 'The catalog has not been built yet',
          'It builds itself every night. An admin can build it now from the Content Library settings. This is not a missing product problem.', retry));
      } else {
        root.appendChild(notice('error', 'The catalog could not load',
          (err && err.message ? err.message + '. ' : '') + 'This is a loading problem, not an empty catalog.', retry));
      }
    });
  }

  function renderShell() {
    clear(root);
    tabsEl = el('nav', { class: 'ccl-tabs', role: 'tablist', 'aria-label': 'Content Library' });
    browseEl = el('div', { class: 'ccl-browse' });
    panelEl = el('div', { class: 'ccl-panel', hidden: true });
    productEl = el('div', { class: 'ccl-product', hidden: true });
    root.appendChild(tabsEl);
    root.appendChild(browseEl);
    root.appendChild(panelEl);
    root.appendChild(productEl);
    renderTabs();
    renderBrowse();
    loadBlogList().catch(function () { /* the Blog images tab shows the error when opened */ });
  }

  /* CUSTOM_CONTENTLIBRARY_TABS_BY_CLAUDE: the tab bar. A count rides on the tabs whose size the catalog already knows. */
  function tabCount(id) {
    var idx = state.index || {};
    if (id === 'logos') return Array.isArray(idx.logos) ? idx.logos.length : null;
    if (id === 'brand') return Array.isArray(idx.brandFiles) ? idx.brandFiles.length : null;
    if (id === 'blog') return state.blog.list ? state.blog.list.length : null;
    if (id === 'banners') return bannerEntries().length;
    return null;
  }
  function renderTabs() {
    clear(tabsEl);
    TABS.forEach(function (t) {
      if (t.id === 'ads' && !canAds()) return;
      var n = tabCount(t.id);
      var on = state.tab === t.id;
      tabsEl.appendChild(el('a', {
        class: 'ccl-tabs__tab' + (on ? ' ccl-tabs__tab--active' : ''), href: t.hash, role: 'tab',
        'aria-selected': on ? 'true' : 'false', 'data-tab': t.id,
      }, [t.label, n != null ? el('span', { class: 'ccl-tabs__n', text: String(n) }) : null]));
    });
    // On a phone the row scrolls sideways: keep the open tab in sight (a deep link can land on the last one).
    var active = tabsEl.querySelector('.ccl-tabs__tab--active');
    if (active && tabsEl.scrollWidth > tabsEl.clientWidth) {
      var left = active.offsetLeft - tabsEl.offsetLeft;
      if (left < tabsEl.scrollLeft || left + active.offsetWidth > tabsEl.scrollLeft + tabsEl.clientWidth) tabsEl.scrollLeft = Math.max(0, left - 16);
    }
  }

  // ── Browse view ─────────────────────────────────────────────────────────────────────────────

  function renderBrowse() {
    var idx = state.index;
    clear(browseEl);
    var age = ageText(idx.builtAt);

    var search = el('input', {
      class: 'ccl-search__input', type: 'search', placeholder: 'Search ' + idx.products.length + ' products by name or brand',
      'aria-label': 'Search products', value: state.query, autocomplete: 'off', spellcheck: 'false',
    });
    search.addEventListener('input', function () { state.query = search.value; renderResults(); });

    // Brand files moved from a button here to their own tab (CUSTOM_CONTENTLIBRARY_TABS_BY_CLAUDE).
    browseEl.appendChild(el('div', { class: 'ccl-toolbar' }, [
      searchField(search),
      ageLine(age),
    ]));

    browseEl.appendChild(renderChips());
    var countRow = el('div', { class: 'ccl-countrow' }, [el('p', { class: 'ccl-count', 'aria-live': 'polite' })]);
    if (hasDates(idx.products)) {
      var sel = el('select', { class: 'ccl-sort__select', 'aria-label': 'Sort products' }, [
        el('option', { value: 'az', text: 'A to Z' }),
        el('option', { value: 'new', text: 'Newest first' }),
      ]);
      sel.value = state.sort === 'new' ? 'new' : 'az';
      sel.addEventListener('change', function () { state.sort = sel.value; writeSort(sel.value); renderResults(); });
      countRow.appendChild(el('label', { class: 'ccl-sort' }, [el('span', { class: 'ccl-sort__label', text: 'Sort:' }), sel]));
    } else {
      state.sort = 'az'; // an index built before dates existed cannot sort by them
    }
    browseEl.appendChild(countRow);
    browseEl.appendChild(el('div', { class: 'ccl-grid' }));
    browseEl.appendChild(el('div', { class: 'ccl-more' }));
    renderResults();
  }

  /* Ryan 2026-10-21: no "Catalog updated ..." line. Only a stale catalog still says so (the nightly refresh failing). */
  function ageLine(age) {
    if (!age.stale) return null;
    return el('p', { class: 'ccl-age ccl-age--stale', text: age.text + '. The nightly refresh may be failing; tell Ryan.' });
  }

  /* The search box with an x that clears it at once (Ryan 2026-10-21). Shown only while there is text; the browser's
     own x is hidden so there is one in every browser and never two. */
  function searchField(search) {
    var x = el('button', { class: 'ccl-search__clear', type: 'button', 'aria-label': 'Clear search', text: '×' });
    var sync = function () { x.hidden = !search.value; };
    x.addEventListener('click', function () {
      search.value = '';
      search.dispatchEvent(new Event('input', { bubbles: true }));
      search.focus();
    });
    search.addEventListener('input', sync);
    sync();
    return el('div', { class: 'ccl-search' }, [search, x]);
  }

  function renderChips() {
    var idx = state.index;
    var wrap = el('div', { class: 'ccl-filters' });
    if (!idx.groups) {
      wrap.appendChild(el('p', { class: 'ccl-filters__off', text: 'Brand and category filters are unavailable right now (' + (idx.groupsError || 'unknown reason') + '). Search still works.' }));
      return wrap;
    }
    var groups = idx.groups;
    if (!state.group && groups.length) state.group = groups[0].t;

    var toggle = el('div', { class: 'ccl-groups', role: 'tablist', 'aria-label': 'Filter by' });
    groups.forEach(function (g) {
      var on = g.t === state.group;
      var picked = typeof state.chips[g.t] === 'number';
      toggle.appendChild(el('button', {
        class: 'ccl-groups__btn' + (on ? ' ccl-groups__btn--active' : '') + (picked ? ' ccl-groups__btn--picked' : ''),
        type: 'button', role: 'tab', 'aria-selected': on ? 'true' : 'false', text: g.t,
        onclick: function () { state.group = g.t; rerenderChips(); },
      }));
    });
    wrap.appendChild(toggle);

    var active = groups.filter(function (g) { return g.t === state.group; })[0];
    var row = el('div', { class: 'ccl-chips' });
    if (active) active.c.forEach(function (chip) {
      var on = state.chips[active.t] === chip.i;
      var n = idx.collections[chip.i] ? idx.collections[chip.i].n : 0;
      row.appendChild(el('button', {
        class: 'ccl-chip' + (on ? ' ccl-chip--active' : ''), type: 'button', 'aria-pressed': on ? 'true' : 'false',
        onclick: function () {
          if (on) delete state.chips[active.t]; else state.chips[active.t] = chip.i;
          rerenderChips(); renderResults();
        },
      }, [chip.t, el('span', { class: 'ccl-chip__n', text: String(n) })]));
    });
    wrap.appendChild(row);

    var picked = Object.keys(state.chips);
    if (picked.length) {
      var summary = el('div', { class: 'ccl-picked' }, [el('span', { class: 'ccl-picked__label', text: 'Showing:' })]);
      picked.forEach(function (g) {
        var ci = state.chips[g];
        var chip = (groups.filter(function (x) { return x.t === g; })[0] || { c: [] }).c.filter(function (c) { return c.i === ci; })[0];
        summary.appendChild(el('button', {
          class: 'ccl-picked__pill', type: 'button', 'aria-label': 'Remove filter ' + (chip ? chip.t : ''),
          onclick: function () { delete state.chips[g]; rerenderChips(); renderResults(); },
        }, [g + ': ' + (chip ? chip.t : '?'), el('span', { class: 'ccl-picked__x', 'aria-hidden': 'true', text: '×' })]));
      });
      summary.appendChild(el('button', { class: 'ccl-link', type: 'button', text: 'Clear filters',
        onclick: function () { state.chips = {}; rerenderChips(); renderResults(); } }));
      wrap.appendChild(summary);
    }
    return wrap;
  }
  function rerenderChips() {
    var old = browseEl.querySelector('.ccl-filters');
    if (old) browseEl.replaceChild(renderChips(), old);
  }

  function currentResults() {
    var idx = state.index;
    var chipped = filterByChips(idx.products, state.chips);
    var found = chipped;
    if (state.query) {
      var keys = chipped === idx.products ? state.keys : chipped.map(searchKey);
      found = searchProducts(chipped, keys, state.query);
    }
    return sortProducts(found, state.sort);
  }

  /** Re-filters and redraws from the top. "Show more" appends instead (`showMore`), so cards
   *  already on screen keep their loaded thumbnails. */
  function renderResults() {
    var grid = browseEl.querySelector('.ccl-grid');
    var count = browseEl.querySelector('.ccl-count');
    state.list = currentResults();
    state.shown = 0;
    clear(grid);
    var total = state.index.products.length;
    var list = state.list;
    count.textContent = list.length === total ? total + ' products' : list.length + ' of ' + total + ' products';
    if (!list.length) {
      clear(browseEl.querySelector('.ccl-more'));
      if (observer) { observer.disconnect(); observer = null; }
      grid.appendChild(notice('empty', 'No products match',
        'Nothing in the live catalog matches ' + (state.query ? '“' + state.query + '”' : 'these filters') + '. Try fewer words or clear a filter.'));
      return;
    }
    showMore();
  }

  function showMore() {
    var grid = browseEl.querySelector('.ccl-grid');
    var more = browseEl.querySelector('.ccl-more');
    var list = state.list;
    var next = Math.min(list.length, state.shown + PAGE_SIZE);
    var frag = document.createDocumentFragment();
    for (var i = state.shown; i < next; i++) frag.appendChild(card(list[i]));
    grid.appendChild(frag);
    state.shown = next;
    clear(more);
    if (observer) { observer.disconnect(); observer = null; }
    if (list.length > state.shown) {
      var btn = el('button', { class: 'ccl-btn ccl-btn--ghost', type: 'button', text: 'Show more (' + (list.length - state.shown) + ' left)', onclick: showMore });
      more.appendChild(btn);
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(function (entries) {
          if (entries.some(function (e) { return e.isIntersecting; })) showMore();
        }, { rootMargin: '600px' });
        observer.observe(btn);
      }
    }
  }

  function card(p) {
    var img = p.img
      ? el('img', { class: 'ccl-card__img', src: thumbUrl(p.img, 400), alt: '', loading: 'lazy', decoding: 'async', width: '400', height: '400' })
      : el('div', { class: 'ccl-card__noimg', text: 'No photo' });
    return el('a', { class: 'ccl-card', href: '#p=' + encodeURIComponent(p.h) }, [
      el('div', { class: 'ccl-card__media' }, [img]),
      el('div', { class: 'ccl-card__body' }, [
        el('p', { class: 'ccl-card__vendor', text: p.v }),
        el('p', { class: 'ccl-card__title', text: p.t }),
        state.sort === 'new' && p.d ? el('p', { class: 'ccl-card__added', text: addedLabel(p.d) }) : null,
      ]),
    ]);
  }

  // ── Product view ────────────────────────────────────────────────────────────────────────────

  var scrollBeforeProduct = 0;

  /* CUSTOM_CONTENTLIBRARY_TABS_BY_CLAUDE: one router for every tab. A tab's browse view stays on screen (its search and
     scroll kept) while an item from it is open; Back returns to it. */
  var BACK_LABEL = { products: 'All products', blog: 'All blog posts', banners: 'All collection pages' };
  function route() {
    if (!state.index) return;
    closeViewer();
    var r = parseRoute(location.hash);
    if (r.tab === 'ads' && !canAds()) {
      // a link to Create ads, for someone it is not switched on for: the library's first tab instead
      try { history.replaceState(null, '', location.pathname + location.search); } catch (e) { /* ignore */ }
      r = parseRoute('');
    }
    var tabChanged = state.tab !== r.tab;
    state.tab = r.tab;
    renderTabs();
    if (r.tab === 'ads') { // CUSTOM_CONTENTLIBRARY_ADS_BY_CLAUDE: the ad maker keeps its own screen, item or not
      state.routed = true;
      productEl.hidden = true; state.product = null; state.set = null; clear(productEl);
      browseEl.hidden = true; panelEl.hidden = false;
      if (tabChanged || !adCtl) renderAds(r.item); else adCtl.show(r.item);
      if (tabChanged) window.scrollTo(0, 0);
      return;
    }
    if (r.item) {
      if (productEl.hidden) {
        scrollBeforeProduct = window.scrollY || 0;
        state.fromGrid = state.routed === true && !tabChanged; // a deep link lands here with no grid behind it
      }
      state.routed = true;
      browseEl.hidden = true; panelEl.hidden = true; productEl.hidden = false;
      if (r.tab === 'products') openProduct(r.item);
      else if (r.tab === 'blog') openArticle(r.item);
      else if (/^pg:/.test(r.item)) openFacilityPage(r.item.slice(3));
      else openCollection(r.item);
      window.scrollTo(0, 0);
      return;
    }
    var wasItem = !productEl.hidden;
    state.routed = true;
    productEl.hidden = true;
    state.product = null; state.set = null; clear(productEl);
    if (r.tab === 'products') { panelEl.hidden = true; browseEl.hidden = false; }
    else { browseEl.hidden = true; panelEl.hidden = false; if (tabChanged || !panelEl.firstChild) renderPanel(r.tab); }
    window.scrollTo(0, wasItem && !tabChanged ? scrollBeforeProduct : 0);
  }

  function backLink() {
    var tab = state.tab || 'products';
    var home = (TABS.filter(function (t) { return t.id === tab; })[0] || TABS[0]).hash;
    return el('a', { class: 'ccl-back', href: home, onclick: function (e) {
      e.preventDefault();
      if (state.fromGrid) history.back(); // back to the grid, scroll position and all
      else location.hash = home === '#' ? '' : home;
    } }, ['\u2190 ' + (BACK_LABEL[tab] || 'Back')]);
  }

  function renderPanel(tab) {
    clear(panelEl);
    adCtl = null;
    state.set = null;
    if (tab === 'blog') renderBlogBrowse();
    else if (tab === 'banners') renderBannersBrowse();
    else if (tab === 'logos') renderLogos();
    else renderBrand();
  }

  /* CUSTOM_CONTENTLIBRARY_ADS_BY_CLAUDE (Ryan 2026-10-21): the Create ads tab. Its code is its own asset, loaded with a
     plain <script> tag the first time the tab opens (⛔ never import(): the CDN minifier turns it into a require() that
     hangs). It gets the library's own helpers, so the search, the facts and the photo reading are these ones. */
  var adCtl = null, adLib = null;
  var AD_TIMEOUT_MS = 20000;
  function loadAdMaker() {
    if (window.ContentAdMaker && window.ContentAdMaker.mount) return Promise.resolve(window.ContentAdMaker);
    if (!adLib) {
      if (!AD_MAKER) return Promise.reject(new Error('the ad maker’s address is missing from the page'));
      adLib = new Promise(function (resolve, reject) {
        var timer = setTimeout(function () { reject(new Error('the ad maker did not load within 20 seconds')); }, AD_TIMEOUT_MS);
        var s = document.createElement('script');
        s.src = AD_MAKER;
        s.async = true;
        s.onload = function () {
          clearTimeout(timer);
          if (window.ContentAdMaker && window.ContentAdMaker.mount) resolve(window.ContentAdMaker); else reject(new Error('the ad maker loaded but did not start'));
        };
        s.onerror = function () { clearTimeout(timer); reject(new Error('the ad maker could not be loaded')); };
        document.head.appendChild(s);
      }).catch(function (e) { adLib = null; throw e; });
    }
    return adLib;
  }
  function adCtx() {
    var parse = function (html) { return new DOMParser().parseFromString(html, 'text/html'); };
    return {
      index: state.index, api: api, el: el, clear: clear, notice: notice,
      searchProducts: function (q) { return searchProducts(state.index.products, state.keys, q); },
      keyFacts: function (html) { return keyFacts(html, parse); }, optionLines: optionLines, splitMedia: splitMedia,
      pageHtml: pageHtml, pageExtras: pageExtras, pageImages: pageImages, pageText: pageText, COLLECTION_PAGE: COLLECTION_PAGE,
      brandSetFor: brandSetFor, photoTag: photoTag, thumbUrl: thumbUrl, loadZip: loadZip, saveBlob: saveBlob,
      assets: { engine: AD_MAKER, montserrat: AD_ASSETS.montserrat, barlow600: AD_ASSETS.barlow600, barlow700: AD_ASSETS.barlow700, bsLogo: AD_ASSETS.bsLogo, bsLogoDark: AD_ASSETS.bsLogoDark, wdPool: AD_ASSETS.wdPool },
      go: function (item) { location.hash = item ? '#ads=' + encodeURIComponent(item) : '#ads'; },
      // the review PDF is for admins and agency users (Ryan 2026-10-27, D22; the worker checks too)
      isAdmin: function () { try { var s = JSON.parse(sessionStorage.getItem('rep_portal_session') || 'null'); return !!(s && s.admin); } catch (e) { return false; } },
      role: function () { try { var s = JSON.parse(sessionStorage.getItem('rep_portal_session') || 'null'); return (s && s.role) || ''; } catch (e) { return ''; } },
    };
  }
  /* CUSTOM_CONTENTLIBRARY_ADS_BY_CLAUDE (Ryan 2026-10-22, Phase 4 step 5): "Create ads" beside "View on site" on an
     opened product or collection, straight to the Create ads tab with that item's set drawn. A brand's own collection
     (ECORE Athletic) goes as c:<handle> too; the ad maker turns it into the brand (v:), the way its search does.
     Hidden (not missing) for anyone without Create ads, and shown or hidden again when the page config arrives. */
  function adsHash(item) { return '#ads=' + encodeURIComponent(item); }
  function adsButton(item) {
    return el('a', { class: 'ccl-btn', href: adsHash(item), 'data-ads-btn': '1', hidden: !canAds() }, ['Create ads']);
  }
  function renderAds(item) {
    clear(panelEl);
    adCtl = null;
    panelEl.appendChild(notice('loading', 'Loading the ad maker…'));
    loadAdMaker().then(function (AM) {
      if (state.tab !== 'ads') return;
      clear(panelEl);
      adCtl = AM.mount(panelEl, adCtx());
      adCtl.show(parseRoute(location.hash).item);
    }).catch(function (e) {
      if (state.tab !== 'ads') return;
      clear(panelEl);
      panelEl.appendChild(notice('error', 'The ad maker could not load', (e && e.message ? e.message + '. ' : '') + 'This is a loading problem. Reload the page; if it persists, tell Ryan.',
        el('button', { class: 'ccl-btn', type: 'button', text: 'Try again', onclick: function () { renderAds(item); } })));
    });
  }
  /* END CUSTOM_CONTENTLIBRARY_ADS_BY_CLAUDE */

  function renderBrand() {
    clear(panelEl);
    state.product = null;
    var files = (state.index && state.index.brandFiles) || [];
    panelEl.appendChild(el('h2', { class: 'ccl-facts__title', text: 'Brand files' }));
    panelEl.appendChild(el('p', { class: 'ccl-gallery__hint', text: 'Our logos and the brand guide. Each one downloads as the original file, exactly as made.' }));
    if (!files.length) {
      panelEl.appendChild(notice('empty', 'No brand files yet', 'None are in the catalog right now. Tell Ryan.'));
      return;
    }
    panelEl.appendChild(el('div', { class: 'ccl-brandgrid' }, files.map(brandCard)));
  }

  /* A photo card for an article or a collection page: the picture fills the card (these are photos, not product cut-outs). */
  /* CUSTOM_CONTENTLIBRARY_COLLECTIONIMAGE_BY_CLAUDE (Ryan 2026-10-21): a collection card is square. A brand shows its
     vendor logo on white (`o.logo`); a category shows its page banner, right-aligned and filling the square top to
     bottom (`o.square`), so a wide 4:1 banner is asked for wide enough to stay sharp. */
  /* A theme image is named in the template as uploaded (Skating-Rink-Floor.webp) but Shopify may serve it lower-cased
     (skating-rink-floor.webp), and the image server is case-sensitive. A picture that fails is tried once lower-cased. */
  function lowerCaseFileUrl(src) {
    var s = String(src || ''), q = s.indexOf('?'), path = q === -1 ? s : s.slice(0, q), rest = q === -1 ? '' : s.slice(q);
    var cut = path.lastIndexOf('/') + 1, name = path.slice(cut);
    return name === name.toLowerCase() ? null : path.slice(0, cut) + name.toLowerCase() + rest;
  }
  function retryLowerCase(img) {
    img.addEventListener('error', function onErr() {
      img.removeEventListener('error', onErr);
      var next = lowerCaseFileUrl(img.getAttribute('src'));
      if (next) img.setAttribute('src', next);
    });
  }
  function photoCard(o) {
    var media = 'ccl-card__media ccl-card__media--photo', img;
    if (o.logo) {
      media = 'ccl-card__media ccl-card__media--logo';
      img = el('img', { class: 'ccl-card__img ccl-card__img--logo', src: thumbUrl(o.logo, 500), alt: '', loading: 'lazy', decoding: 'async' });
    } else if (o.img) {
      if (o.square) media += ' ccl-card__media--square';
      if (o.edgeRight) media += ' ccl-card__media--edge';
      img = el('img', { class: 'ccl-card__img ccl-card__img--photo', src: thumbUrl(o.img, o.square ? 1200 : 500), alt: '', loading: 'lazy', decoding: 'async' });
      retryLowerCase(img);
    } else img = el('div', { class: 'ccl-card__noimg', text: 'No picture' });
    return el('a', { class: 'ccl-card ccl-card--photo', href: o.href }, [
      el('div', { class: media }, [img]),
      el('div', { class: 'ccl-card__body' }, [
        o.kicker ? el('p', { class: 'ccl-card__vendor', text: o.kicker }) : null,
        el('p', { class: 'ccl-card__title', text: o.title }),
        o.sub ? el('p', { class: 'ccl-card__price', text: o.sub }) : null,
      ]),
    ]);
  }

  /* A grid that shows 60 at a time and adds more as you scroll, for the blog and collection tabs. */
  function pagedGrid(host, list, cardFor, holder) {
    var grid = el('div', { class: 'ccl-grid' });
    var more = el('div', { class: 'ccl-more' });
    host.appendChild(grid);
    host.appendChild(more);
    var shown = 0, obs = null;
    function step() {
      var next = Math.min(list.length, shown + PAGE_SIZE);
      var frag = document.createDocumentFragment();
      for (var i = shown; i < next; i++) frag.appendChild(cardFor(list[i]));
      grid.appendChild(frag);
      shown = next;
      clear(more);
      if (obs) { obs.disconnect(); obs = null; }
      if (list.length > shown) {
        var btn = el('button', { class: 'ccl-btn ccl-btn--ghost', type: 'button', text: 'Show more (' + (list.length - shown) + ' left)', onclick: step });
        more.appendChild(btn);
        if ('IntersectionObserver' in window) {
          obs = new IntersectionObserver(function (entries) { if (entries.some(function (e) { return e.isIntersecting; })) step(); }, { rootMargin: '600px' });
          obs.observe(btn);
        }
      }
      if (holder) holder.obs = obs;
    }
    step();
    return grid;
  }

  // \u2500\u2500 Blog images tab \u2500\u2500
  function loadBlogList() {
    var b = state.blog;
    if (b.list) return Promise.resolve(b.list);
    if (b.loading) return b.loading;
    var getText = function (path) {
      return fetch(path, { headers: { Accept: 'application/xml,text/xml' }, credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); });
    };
    b.error = null;
    b.loading = getText('/sitemap.xml').then(function (index) {
      var paths = articleSitemapPaths(index);
      if (!paths.length) return [];
      return Promise.all(paths.map(getText)).then(function (files) {
        var all = [];
        files.forEach(function (x) { all = all.concat(parseArticleSitemap(x)); });
        return sortArticles(all);
      });
    }).then(function (list) { b.list = list; b.loading = null; renderTabs(); return list; })
      .catch(function (e) { b.loading = null; b.error = (e && e.message) || 'unknown error'; throw e; });
    return b.loading;
  }

  function renderBlogBrowse() {
    clear(panelEl);
    var b = state.blog;
    if (!b.list) {
      panelEl.appendChild(notice('loading', 'Loading the blog posts\u2026'));
      loadBlogList().then(function () { if (state.tab === 'blog' && productEl.hidden) renderBlogBrowse(); })
        .catch(function () {
          if (state.tab !== 'blog' || !productEl.hidden) return;
          clear(panelEl);
          panelEl.appendChild(notice('error', 'The blog posts could not load',
            'The website did not answer (' + b.error + '). This is a loading problem, not an empty blog.',
            el('button', { class: 'ccl-btn', type: 'button', text: 'Try again', onclick: renderBlogBrowse })));
        });
      return;
    }
    var search = el('input', {
      class: 'ccl-search__input', type: 'search', placeholder: 'Search ' + b.list.length + ' blog posts by title',
      'aria-label': 'Search blog posts', value: b.query, autocomplete: 'off', spellcheck: 'false',
    });
    panelEl.appendChild(el('div', { class: 'ccl-toolbar' }, [searchField(search)]));
    var blogs = [];
    b.list.forEach(function (a) { if (blogs.indexOf(a.blog) === -1) blogs.push(a.blog); });
    var chips = el('div', { class: 'ccl-chips ccl-filters' });
    if (blogs.length > 1) {
      [''].concat(blogs).forEach(function (h) {
        var n = h ? b.list.filter(function (a) { return a.blog === h; }).length : b.list.length;
        chips.appendChild(el('button', {
          class: 'ccl-chip' + (b.filter === h ? ' ccl-chip--active' : ''), type: 'button', 'aria-pressed': b.filter === h ? 'true' : 'false',
          onclick: function () { b.filter = h; renderBlogBrowse(); },
        }, [h ? blogLabel(h) : 'All blogs', el('span', { class: 'ccl-chip__n', text: String(n) })]));
      });
      panelEl.appendChild(chips);
    }
    var count = el('p', { class: 'ccl-count', 'aria-live': 'polite' });
    panelEl.appendChild(el('div', { class: 'ccl-countrow' }, [count]));
    var host = el('div');
    panelEl.appendChild(host);
    var holder = {};
    function draw() {
      clear(host);
      if (holder.obs) holder.obs.disconnect();
      var pool = b.filter ? b.list.filter(function (a) { return a.blog === b.filter; }) : b.list;
      var list = searchTitles(pool, b.query, function (a) { return a.title + ' ' + blogLabel(a.blog); });
      count.textContent = list.length === b.list.length ? b.list.length + ' blog posts' : list.length + ' of ' + b.list.length + ' blog posts';
      if (!list.length) {
        host.appendChild(notice('empty', 'No blog posts match', 'Nothing on the blog matches ' + (b.query ? '\u201c' + b.query + '\u201d' : 'this filter') + '. Try fewer words.'));
        return;
      }
      pagedGrid(host, list, function (a) {
        return photoCard({ href: '#a=' + encodeURIComponent(a.blog + '/' + a.handle), img: a.img, kicker: blogLabel(a.blog), title: a.title,
          sub: a.lastmod ? 'Updated ' + new Date(a.lastmod).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Denver' }) : '' });
      }, holder);
    }
    search.addEventListener('input', function () { b.query = search.value; draw(); });
    draw();
  }

  // \u2500\u2500 Collection images tab \u2500\u2500
  /* Every live collection a shopper can browse to: the link-excluded utility ones (home page, fees, seasonal) are left out. */
  function bannerCollections() {
    var idx = state.index || {};
    return (idx.collections || []).filter(function (c) { return c && !c.x; });
  }
  /* CUSTOM_CONTENTLIBRARY_FACILITYPAGES_BY_CLAUDE (Ryan 2026-10-21): the pages a collection's tiles link to (ECORE Change
     Rooms, ECORE Hotels ...) sit in this tab beside the collections, in title order. */
  function bannerEntries() {
    var pages = ((state.index || {}).pages || []).map(function (p) { return { h: p.h, t: p.t, img: p.img, page: true }; });
    return bannerCollections().concat(pages).sort(function (a, b) { return String(a.t).localeCompare(String(b.t)); });
  }
  function renderBannersBrowse() {
    clear(panelEl);
    var s = state.banners;
    var all = bannerEntries();
    var search = el('input', {
      class: 'ccl-search__input', type: 'search', placeholder: 'Search ' + all.length + ' collection pages',
      'aria-label': 'Search collection pages', value: s.query, autocomplete: 'off', spellcheck: 'false',
    });
    panelEl.appendChild(el('div', { class: 'ccl-toolbar' }, [searchField(search), ageLine(ageText(state.index.builtAt))]));
    var count = el('p', { class: 'ccl-count', 'aria-live': 'polite' });
    panelEl.appendChild(el('div', { class: 'ccl-countrow' }, [count]));
    var host = el('div');
    panelEl.appendChild(host);
    var holder = {};
    function draw() {
      clear(host);
      if (holder.obs) holder.obs.disconnect();
      var list = searchTitles(all, s.query, function (c) { return c.t + ' ' + String(c.h).replace(/-/g, ' '); });
      count.textContent = list.length === all.length ? all.length + ' collection pages' : list.length + ' of ' + all.length + ' collection pages';
      if (!all.length) { host.appendChild(notice('empty', 'No collection pages', 'The catalog lists none right now. Tell Ryan.')); return; }
      if (!list.length) { host.appendChild(notice('empty', 'No collection pages match', 'Nothing matches \u201c' + s.query + '\u201d. Try fewer words.')); return; }
      pagedGrid(host, list, function (c) {
        if (c.page) return photoCard({ href: '#pg=' + encodeURIComponent(c.h), img: c.img || null, square: true, edgeRight: true, title: c.t, sub: 'Facility page' });
        return photoCard({ href: '#c=' + encodeURIComponent(c.h), img: c.img || null, logo: c.logo || null, square: true, title: c.t, sub: c.n + ' product' + (c.n === 1 ? '' : 's') });
      }, holder);
    }
    search.addEventListener('input', function () { s.query = search.value; draw(); });
    draw();
  }

  // \u2500\u2500 Vendor logos tab \u2500\u2500
  function renderLogos() {
    clear(panelEl);
    var idx = state.index;
    panelEl.appendChild(el('h2', { class: 'ccl-facts__title', text: 'Vendor logos' }));
    panelEl.appendChild(el('p', { class: 'ccl-gallery__hint', text: 'Logos of the brands we sell on the website, from the Vendor Guide. A brand shows here once it has a live product.' }));
    if (idx.logos === undefined) {
      panelEl.appendChild(notice('empty', 'Vendor logos arrive with the next catalog update', 'The catalog was built before this tab existed. It refreshes every night.'));
      return;
    }
    if (idx.logos === null) {
      panelEl.appendChild(notice('error', 'The vendor logos could not load', (idx.logosError || 'The Vendor Guide did not answer') + '. This is a loading problem, not a missing logo. Tell Ryan.'));
      return;
    }
    if (!idx.logos.length) {
      panelEl.appendChild(notice('empty', 'No vendor logos yet', 'No brand on the website has a logo in the Vendor Guide. Tell Ryan.'));
      return;
    }
    var images = idx.logos.map(function (l) { return { src: l.url, alt: l.n + ' logo', caption: l.n, fileBase: logoFileBase(l.n) }; });
    var gallery = el('section', { class: 'ccl-gallery ccl-gallery--logos', 'aria-label': 'Vendor logos' });
    panelEl.appendChild(gallery);
    imageSet(gallery, panelEl, {
      kind: 'logos', base: 'vendor-logos', zip: 'blue-sky-vendor-logos.zip', images: images,
      hint: function (n) { return n + ' logo' + (n === 1 ? '' : 's') + '. Tap a logo to see it large, or its box to select it. PNG keeps a see-through background where the logo has one.'; },
    });
  }

  function brandCard(f) {
    var isImage = /^image\//.test(f.mt || '') || /\.(png|jpe?g|svg|webp)$/i.test(f.f);
    var status = el('p', { class: 'ccl-brand__status', 'aria-live': 'polite' });
    var btn = el('button', { class: 'ccl-btn', type: 'button', text: 'Download' });
    btn.addEventListener('click', function () {
      btn.disabled = true; status.className = 'ccl-brand__status'; status.textContent = 'Downloading…';
      fetch(f.url, { mode: 'cors', credentials: 'omit' })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.blob(); })
        .then(function (blob) { saveBlob(blob, brandDownloadName(f.f)); status.textContent = 'Saved ' + brandDownloadName(f.f) + '.'; })
        .catch(function (e) { status.className = 'ccl-brand__status ccl-brand__status--error'; status.textContent = 'Download failed (' + (e && e.message) + '). Try again.'; })
        .then(function () { btn.disabled = false; });
    });
    return el('div', { class: 'ccl-brand' }, [
      el('div', { class: 'ccl-brand__media' + (isImage ? '' : ' ccl-brand__media--doc') }, [
        isImage ? el('img', { class: 'ccl-brand__img', src: f.url, alt: f.n, loading: 'lazy', decoding: 'async' })
          : el('span', { class: 'ccl-brand__doc', text: fileTypeLabel(f.f, f.mt) }),
      ]),
      el('p', { class: 'ccl-brand__name', text: f.n }),
      el('p', { class: 'ccl-brand__meta', text: [fileTypeLabel(f.f, f.mt), f.w ? f.w + '×' + f.h : '', fileSizeLabel(f.sz)].filter(Boolean).join(' · ') }),
      btn, status,
    ]);
  }

  function openProduct(handle) {
    clear(productEl);
    state.selected = {};
    var entry = state.byHandle[handle];
    productEl.appendChild(backLink());
    productEl.appendChild(notice('loading', 'Loading ' + (entry ? entry.t : 'product') + '…'));
    fetch('/products/' + encodeURIComponent(handle) + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        if (r.status === 404) { var e = new Error('not-found'); e.notFound = true; throw e; }
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (prod) {
        if (parseHash(location.hash) !== handle) return; // navigated away meanwhile
        state.product = prod;
        renderProduct(prod, entry);
      })
      .catch(function (err) {
        if (parseHash(location.hash) !== handle) return;
        clear(productEl);
        productEl.appendChild(backLink());
        productEl.appendChild(err && err.notFound
          ? notice('empty', 'This product is no longer on the website', 'It may have been unpublished since the catalog was last updated.')
          : notice('error', 'This product could not load', 'The website did not answer (' + (err && err.message) + '). Try again in a moment.',
              el('button', { class: 'ccl-btn', type: 'button', text: 'Try again', onclick: function () { openProduct(handle); } })));
      });
  }

  function renderProduct(prod, entry) {
    clear(productEl);
    productEl.appendChild(backLink());
    var idx = state.index;
    var facts = keyFacts(prod.description, function (html) { return new DOMParser().parseFromString(html, 'text/html'); });
    var options = optionLines(prod.options, 12);
    var media = splitMedia(prod.media);
    var price = prod.price_varies ? 'From ' + money(prod.price_min) : money(prod.price_min);

    var colLinks = entry ? entry.c.map(function (i) { return idx.collections[i]; }).filter(function (c) { return c && !c.x; }) : [];

    var info = el('section', { class: 'ccl-facts', 'aria-label': 'Key facts' }, [
      el('p', { class: 'ccl-facts__vendor', text: prod.vendor || '' }),
      el('h2', { class: 'ccl-facts__title', text: prod.title }),
      el('p', { class: 'ccl-facts__price', text: price }),
      facts.intro ? el('p', { class: 'ccl-facts__intro', text: facts.intro }) : null,
      facts.bullets.length ? el('ul', { class: 'ccl-facts__bullets' }, facts.bullets.map(function (b) { return el('li', { text: b }); })) : null,
      options.length ? el('div', { class: 'ccl-facts__options' }, options.map(function (o) {
        return el('p', { class: 'ccl-facts__option' }, [
          el('span', { class: 'ccl-facts__option-name', text: o.name + ': ' }),
          o.values.join(', ') + (o.more > 0 ? ' +' + o.more + ' more' : ''),
        ]);
      })) : null,
      el('div', { class: 'ccl-facts__actions' }, [
        el('a', { class: 'ccl-btn', href: '/products/' + prod.handle, target: '_blank', rel: 'noopener' }, ['View on site ↗']),
        adsButton('p:' + prod.handle),
      ]),
      colLinks.length ? el('div', { class: 'ccl-facts__cols' }, [
        el('p', { class: 'ccl-facts__cols-label', text: 'In collections' }),
        el('div', { class: 'ccl-facts__pills' }, colLinks.map(function (c) {
          return el('a', { class: 'ccl-pill', href: '/collections/' + c.h, target: '_blank', rel: 'noopener', text: c.t });
        })),
      ]) : null,
    ]);

    // One grid in page order: the gallery first (it tops every product page), then the rest of the page
    // as it arrives. `images` is shared with the action bar, so Select all and Download see the extras.
    var tags = brandSetFor(idx, entry);
    var gallery = el('section', { class: 'ccl-gallery', 'aria-label': 'Photos' });
    productEl.appendChild(el('div', { class: 'ccl-product__layout' }, [info, gallery]));
    imageSet(gallery, productEl, {
      kind: 'photos', base: prod.handle, images: media.images, videos: media.videos, tags: tags,
      hint: function (n, loading) {
        return n + ' photo' + (n === 1 ? '' : 's') + (loading ? ' so far, finding more on the product page…' : '') + '. Tap a photo to see it large, or its box to select it; once one is selected, tapping any photo selects it. Then pick a format and Download. Every file is full size.' +
          (tags ? ' PRODUCT photos are this product\'s own; BRAND photos are shared by every product with this page design.' : '');
      },
      loadingText: 'Finding photos on the product page…',
      empty: ['No photos on this product', 'The product page has no images to download.'],
      more: function (images) {
        return pageHtml('/products/' + encodeURIComponent(prod.handle)).then(function (doc) { return pageExtras(doc, images); });
      },
      moreFailed: function (n, err) {
        return (n ? 'Showing the main ' + n + ' photo' + (n === 1 ? '' : 's') + ' only: ' : '') +
          'the rest of the product page could not be read (' + (err && err.message) + '). Reopen the product to try again.';
      },
    });
  }

  /** A live storefront page, parsed (same origin, the shopper's view). */
  function pageHtml(path) {
    return fetch(path, { headers: { Accept: 'text/html' }, credentials: 'same-origin' })
      .then(function (r) { if (r.status === 404) { var e = new Error('not-found'); e.notFound = true; throw e; } if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) { return new DOMParser().parseFromString(html, 'text/html'); });
  }

  /* CUSTOM_CONTENTLIBRARY_TABS_BY_CLAUDE: one photo set for every tab - the tiles, the large view, Select all, the
     format and the ZIP. `host` is the screen the set lives on (its download bar goes there).
       kind   'photos' | 'logos' (logos default to PNG and keep their see-through background)
       base   the ZIP's name and each file's name: <base>-01.jpg ... <base>-images.zip; `zip` names the ZIP outright
       images the first tiles; `more(images)` -> a promise of { images, videos } added in page order
       tags   the BRAND set (products only); hint(n, loading), loadingText, empty [title, lead], moreFailed(n, err) */
  function imageSet(gallery, host, o) {
    var set = { kind: o.kind || 'photos', base: o.base, zip: o.zip || null, host: host };
    state.set = set;
    state.selected = {};
    state.format = readFormat(set.kind);
    var tags = o.tags || null;
    var images = (o.images || []).slice();
    state.images = images; // the large view walks the same list, extras included as they arrive
    images.forEach(function (m) { m.tag = photoTag(m.src, tags); });
    var hint = el('p', { class: 'ccl-gallery__hint' });
    var grid = el('div', { class: 'ccl-tiles' + (set.kind === 'logos' ? ' ccl-tiles--logos' : '') });
    var videoBox = el('div', { class: 'ccl-videos' });
    images.forEach(function (m, pos) { grid.appendChild(tile(m, pos)); });
    gallery.appendChild(hint);
    gallery.appendChild(grid);
    gallery.appendChild(videoBox);
    var videos = (o.videos || []).slice();
    function paint(loading) {
      var n = images.length;
      hint.className = 'ccl-gallery__hint';
      hint.textContent = n ? o.hint(n, loading) : (loading ? (o.loadingText || 'Loading…') : '');
      clear(videoBox);
      if (videos.length) {
        videoBox.appendChild(el('p', { class: 'ccl-facts__cols-label', text: 'Videos' }));
        videoBox.appendChild(el('ul', {}, videos.map(function (v) { return el('li', {}, [el('a', { href: v.url, target: '_blank', rel: 'noopener', text: v.label })]); })));
      }
      if (!loading && !n && o.empty && !gallery.querySelector('.ccl-notice')) gallery.insertBefore(notice('empty', o.empty[0], o.empty[1]), grid);
      if (n && !host.querySelector('.ccl-bar')) host.appendChild(actionBar(images));
      updateBar();
    }
    paint(!!o.more);
    if (!o.more) return;
    o.more(images).then(function (extra) {
      if (state.set !== set) return; // another set opened meanwhile
      extra.images.forEach(function (m) { m.tag = photoTag(m.src, tags); grid.appendChild(tile(m, images.length)); images.push(m); });
      videos = videos.concat(extra.videos.filter(function (v) { return !videos.some(function (w) { return w.url === v.url; }); }));
      paint(false);
    }).catch(function (err) {
      if (state.set !== set) return;
      paint(false);
      hint.className = 'ccl-gallery__hint ccl-gallery__hint--warn';
      hint.textContent = o.moreFailed(images.length, err);
    });
  }
  function setHost() { return (state.set && state.set.host) || productEl; }

  // ── One blog article (#a=<blog>/<article>) ──
  function openArticle(key) {
    clear(productEl);
    state.selected = {}; state.set = null;
    var parts = key.split('/');
    var blog = parts[0], handle = parts[1];
    var path = '/blogs/' + encodeURIComponent(blog) + '/' + encodeURIComponent(handle);
    var known = (state.blog.list || []).filter(function (a) { return a.blog === blog && a.handle === handle; })[0] || null;
    var title = el('h2', { class: 'ccl-facts__title', text: known ? known.title : blogLabel(handle) });
    var info = el('section', { class: 'ccl-facts', 'aria-label': 'About this post' }, [
      el('p', { class: 'ccl-facts__vendor', text: blogLabel(blog) }),
      title,
      known && known.lastmod ? el('p', { class: 'ccl-facts__intro', text: 'Updated ' + new Date(known.lastmod).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Denver' }) }) : null,
      el('div', { class: 'ccl-facts__actions' }, [el('a', { class: 'ccl-btn', href: path, target: '_blank', rel: 'noopener' }, ['View on site ↗'])]),
    ]);
    var gallery = el('section', { class: 'ccl-gallery', 'aria-label': 'Photos' });
    productEl.appendChild(backLink());
    productEl.appendChild(el('div', { class: 'ccl-product__layout' }, [info, gallery]));
    imageSet(gallery, productEl, {
      kind: 'photos', base: handle, images: [],
      hint: function (n) { return n + ' picture' + (n === 1 ? '' : 's') + ' in this post, in the order they appear. Tap one to see it large, or its box to select it. Then pick a format and Download. Every file is full size.'; },
      loadingText: 'Finding the pictures in this post…',
      empty: ['No pictures in this post', 'The post has no images to download.'],
      more: function (images) {
        return pageHtml(path).then(function (doc) {
          var h1 = doc.querySelector('h1');
          if (h1 && !known && parseRoute(location.hash).item === key) title.textContent = (h1.textContent || '').replace(/\s+/g, ' ').trim() || title.textContent;
          return pageImages(doc, images, ARTICLE_PAGE);
        });
      },
      moreFailed: function (n, err) {
        return err && err.notFound ? 'This post is no longer on the website.' : 'The post could not be read (' + (err && err.message) + '). Reopen it to try again.';
      },
    });
  }

  // ── One collection page (#c=<handle>) ──
  function openCollection(handle) {
    clear(productEl);
    state.selected = {}; state.set = null;
    var c = bannerCollections().filter(function (x) { return x.h === handle; })[0]
      || ((state.index && state.index.collections) || []).filter(function (x) { return x.h === handle; })[0] || null;
    var path = '/collections/' + encodeURIComponent(handle);
    var info = el('section', { class: 'ccl-facts', 'aria-label': 'About this collection' }, [
      el('p', { class: 'ccl-facts__vendor', text: 'Collection page' }),
      el('h2', { class: 'ccl-facts__title', text: c ? c.t : handle }),
      c ? el('p', { class: 'ccl-facts__intro', text: c.n + ' product' + (c.n === 1 ? '' : 's') + ' on the website' }) : null,
      el('div', { class: 'ccl-facts__actions' }, [el('a', { class: 'ccl-btn', href: path, target: '_blank', rel: 'noopener' }, ['View on site ↗']), c && c.n ? adsButton('c:' + handle) : null]),
    ]);
    var gallery = el('section', { class: 'ccl-gallery', 'aria-label': 'Pictures' });
    productEl.appendChild(backLink());
    productEl.appendChild(el('div', { class: 'ccl-product__layout' }, [info, gallery]));
    imageSet(gallery, productEl, {
      kind: 'photos', base: handle, images: [],
      hint: function (n) { return n + ' picture' + (n === 1 ? '' : 's') + ' on this collection page, banner first. The product photos and trust badges are left out (the product photos are on the Product images tab). Tap one to see it large, or its box to select it. Every file is full size.'; },
      loadingText: 'Finding the pictures on this collection page…',
      empty: ['No pictures on this collection page', 'The page has no banner or other pictures to download.'],
      more: function (images) { return pageHtml(path).then(function (doc) { return pageImages(doc, images, COLLECTION_PAGE); }); },
      moreFailed: function (n, err) {
        return err && err.notFound ? 'This collection page is no longer on the website.' : 'The collection page could not be read (' + (err && err.message) + '). Reopen it to try again.';
      },
    });
  }

  /* A facility page (/pages/<handle>): only its own pictures. Its product grid, trust badges and the site-wide category
     tiles at the bottom are left out, so today that is the page's top banner. */
  function openFacilityPage(handle) {
    clear(productEl);
    state.selected = {}; state.set = null;
    var p = ((state.index && state.index.pages) || []).filter(function (x) { return x.h === handle; })[0] || null;
    var path = '/pages/' + encodeURIComponent(handle);
    var info = el('section', { class: 'ccl-facts', 'aria-label': 'About this page' }, [
      el('p', { class: 'ccl-facts__vendor', text: 'Facility page' }),
      el('h2', { class: 'ccl-facts__title', text: p ? p.t : handle }),
      el('div', { class: 'ccl-facts__actions' }, [el('a', { class: 'ccl-btn', href: path, target: '_blank', rel: 'noopener' }, ['View on site ↗'])]),
    ]);
    var gallery = el('section', { class: 'ccl-gallery', 'aria-label': 'Pictures' });
    productEl.appendChild(backLink());
    productEl.appendChild(el('div', { class: 'ccl-product__layout' }, [info, gallery]));
    imageSet(gallery, productEl, {
      kind: 'photos', base: handle, images: [],
      hint: function (n) { return n + ' picture' + (n === 1 ? '' : 's') + ' on this page. The product photos, trust badges and category tiles are left out. Tap one to see it large, or its box to select it. Every file is full size.'; },
      loadingText: 'Finding the pictures on this page…',
      empty: ['No pictures on this page', 'The page has no pictures of its own to download.'],
      more: function (images) { return pageHtml(path).then(function (doc) { return pageImages(doc, images, FACILITY_PAGE); }); },
      moreFailed: function (n, err) {
        return err && err.notFound ? 'This page is no longer on the website.' : 'The page could not be read (' + (err && err.message) + '). Reopen it to try again.';
      },
    });
  }

  /* CUSTOM_CONTENTLIBRARY_VIEWER_BY_CLAUDE (Ryan 2026-10-21): with nothing selected, a click on a photo opens it
     large and only its checkbox selects it; once anything is selected, a click anywhere on a photo selects it. */
  function tileClickAction(onCheckbox, selectedCount) {
    return onCheckbox || selectedCount > 0 ? 'toggle' : 'view';
  }

  function tile(m, pos) {
    return el('button', {
      class: 'ccl-tile', type: 'button', 'aria-pressed': 'false', 'data-pos': String(pos),
      'aria-label': 'Photo ' + (pos + 1) + (m.alt ? ': ' + m.alt : ''),
      onclick: function (e) {
        var onBox = !!(e.target && e.target.closest && e.target.closest('.ccl-tile__check'));
        if (tileClickAction(onBox, selectedPositions().length) === 'toggle') toggle(pos, e.currentTarget);
        else openViewer(pos, e.currentTarget);
      },
    }, [
      el('img', { class: 'ccl-tile__img', src: thumbUrl(m.src, 500), alt: m.alt || '', loading: 'lazy', decoding: 'async' }),
      el('span', { class: 'ccl-tile__check', 'aria-hidden': 'true' }),
      m.width ? el('span', { class: 'ccl-tile__size', text: m.width + '×' + m.height }) : null,
      m.tag ? el('span', { class: 'ccl-tile__tag ccl-tile__tag--' + m.tag, text: m.tag === 'brand' ? 'BRAND' : 'PRODUCT' }) : null,
      m.caption ? el('span', { class: 'ccl-tile__caption', text: m.caption }) : null,
    ]);
  }

  // ── The large view ──────────────────────────────────────────────────────────────────────────
  var viewer = null;
  function closeViewer() {
    if (!viewer) return;
    document.removeEventListener('keydown', viewer.onKey);
    viewer.node.remove();
    document.body.style.overflow = viewer.overflow;
    if (viewer.returnTo && viewer.returnTo.focus) viewer.returnTo.focus();
    viewer = null;
  }
  function openViewer(pos, returnTo) {
    var images = state.images || [];
    if (!images[pos]) return;
    closeViewer();
    var img = el('img', { class: 'ccl-viewer__img', alt: '' });
    var cap = el('p', { class: 'ccl-viewer__cap' });
    var pick = el('button', { class: 'ccl-btn ccl-viewer__pick', type: 'button' });
    var node = el('div', { class: 'ccl-viewer', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Photo' }, [
      el('button', { class: 'ccl-viewer__close', type: 'button', 'aria-label': 'Close', text: '×', onclick: closeViewer }),
      el('button', { class: 'ccl-viewer__nav ccl-viewer__nav--prev', type: 'button', 'aria-label': 'Previous photo', text: '‹', onclick: function (e) { e.stopPropagation(); show(cur - 1); } }),
      el('div', { class: 'ccl-viewer__stage' }, [img]),
      el('button', { class: 'ccl-viewer__nav ccl-viewer__nav--next', type: 'button', 'aria-label': 'Next photo', text: '›', onclick: function (e) { e.stopPropagation(); show(cur + 1); } }),
      el('div', { class: 'ccl-viewer__foot' }, [cap, pick]),
    ]);
    var cur = pos;
    /* One picture in the set: both arrows greyed out and do nothing (Ryan 2026-10-21). */
    var single = (state.images || []).length < 2;
    node.querySelectorAll('.ccl-viewer__nav').forEach(function (b) { b.disabled = single; });
    function show(i) {
      var n = (state.images || []).length;
      if (n < 2 && i !== pos) return;
      cur = (i + n) % n;
      var m = state.images[cur];
      img.src = thumbUrl(m.src, 1600);
      img.alt = m.alt || '';
      cap.textContent = (m.caption ? m.caption + ' · ' : '') + 'Photo ' + (cur + 1) + ' of ' + n + (m.tag ? ' · ' + (m.tag === 'brand' ? 'BRAND' : 'PRODUCT') : '') + (m.width ? ' · ' + m.width + '×' + m.height : '');
      pick.textContent = state.selected[cur] ? 'Selected ✓' : 'Select this photo';
      pick.setAttribute('aria-pressed', state.selected[cur] ? 'true' : 'false');
    }
    pick.addEventListener('click', function (e) {
      e.stopPropagation();
      var t = setHost().querySelector('.ccl-tile[data-pos="' + cur + '"]');
      if (t) toggle(cur, t);
      show(cur);
    });
    node.addEventListener('click', function (e) { if (e.target === node || e.target.classList.contains('ccl-viewer__stage')) closeViewer(); });
    var onKey = function (e) {
      if (e.key === 'Escape') closeViewer();
      else if (e.key === 'ArrowLeft') show(cur - 1);
      else if (e.key === 'ArrowRight') show(cur + 1);
    };
    viewer = { node: node, onKey: onKey, returnTo: returnTo, overflow: document.body.style.overflow };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    root.appendChild(node);
    show(pos);
    node.querySelector('.ccl-viewer__close').focus();
  }

  function toggle(pos, btn) {
    if (state.busy) return;
    if (state.selected[pos]) delete state.selected[pos]; else state.selected[pos] = true;
    btn.setAttribute('aria-pressed', state.selected[pos] ? 'true' : 'false');
    updateBar();
  }

  function actionBar(images) {
    var fmt = el('select', { class: 'ccl-sort__select ccl-bar__format', 'aria-label': 'Download format' },
      Object.keys(FORMATS).map(function (k) { return el('option', { value: k, text: FORMATS[k].label }); }));
    fmt.value = state.format;
    fmt.addEventListener('change', function () { state.format = formatOf(fmt.value); writeFormat(state.format, state.set && state.set.kind); updateBar(); });
    return el('div', { class: 'ccl-bar', role: 'region', 'aria-label': 'Download' }, [
      el('span', { class: 'ccl-bar__status', 'aria-live': 'polite' }),
      el('div', { class: 'ccl-bar__btns' }, [
        el('label', { class: 'ccl-sort ccl-bar__fmtwrap', title: '' }, [el('span', { class: 'ccl-sort__label', text: 'Format:' }), fmt]),
        el('button', { class: 'ccl-btn ccl-btn--ghost ccl-bar__all', type: 'button', text: 'Select all', onclick: function () {
          if (state.busy) return;
          images.forEach(function (m, pos) { state.selected[pos] = true; });
          setHost().querySelectorAll('.ccl-tile').forEach(function (t) { t.setAttribute('aria-pressed', 'true'); });
          updateBar();
        } }),
        el('button', { class: 'ccl-btn ccl-btn--ghost ccl-bar__clear', type: 'button', text: 'Clear', onclick: function () {
          if (state.busy) return;
          state.selected = {};
          setHost().querySelectorAll('.ccl-tile').forEach(function (t) { t.setAttribute('aria-pressed', 'false'); });
          updateBar();
        } }),
        el('button', { class: 'ccl-btn ccl-bar__dl', type: 'button', onclick: function () { download(images); } }),
      ]),
    ]);
  }

  function selectedPositions() {
    return Object.keys(state.selected).map(Number).sort(function (a, b) { return a - b; });
  }

  function updateBar(message, kind) {
    var bar = setHost().querySelector('.ccl-bar');
    if (!bar) return;
    var n = selectedPositions().length;
    var dl = bar.querySelector('.ccl-bar__dl');
    dl.textContent = state.busy ? 'Preparing…' : 'Download (' + n + ')';
    dl.disabled = state.busy || n === 0;
    bar.querySelector('.ccl-bar__all').disabled = state.busy;
    bar.querySelector('.ccl-bar__clear').disabled = state.busy || n === 0;
    var sel = bar.querySelector('.ccl-bar__format');
    if (sel) { sel.disabled = state.busy; sel.title = FORMATS[state.format].hint; }
    var st = bar.querySelector('.ccl-bar__status');
    st.textContent = message || (n ? n + ' selected.' : '');
    st.className = 'ccl-bar__status' + (kind ? ' ccl-bar__status--' + kind : '');
  }

  /* ⛔ A PLAIN <script> TAG, NEVER import(). Shopify's CDN minifies theme JS and rewrote
     `import(url)` into `require(url)`, which on this storefront neither loads nor errors: the first
     live download sat on "Downloading 2 of 2" for ever (2026-09-26). The vendored library is a
     classic script that sets window.ClientZip. The timeout turns any future stall into a message. */
  var ZIP_TIMEOUT_MS = 20000;
  var zipLib = null;
  function loadZip() {
    if (window.ClientZip) return Promise.resolve(window.ClientZip);
    if (!zipLib) {
      if (!ZIP_LIB) return Promise.reject(new Error('the ZIP library URL is missing from the page'));
      zipLib = new Promise(function (resolve, reject) {
        var timer = setTimeout(function () { reject(new Error('the ZIP tool did not load within 20 seconds')); }, ZIP_TIMEOUT_MS);
        var s = document.createElement('script');
        s.src = ZIP_LIB;
        s.async = true;
        s.onload = function () {
          clearTimeout(timer);
          if (window.ClientZip) resolve(window.ClientZip); else reject(new Error('the ZIP tool loaded but did not start'));
        };
        s.onerror = function () { clearTimeout(timer); reject(new Error('the ZIP tool could not be loaded')); };
        document.head.appendChild(s);
      }).catch(function (e) { zipLib = null; throw e; });
    }
    return zipLib;
  }

  /** A blob in the wanted type: as fetched when it already is, else redrawn on a canvas. JPG gets a white
   *  background (it has no transparency). A browser that cannot write the type is an error, never a mislabelled file. */
  function toFormat(blob, mime) {
    if (blob.type === mime) return Promise.resolve(blob);
    if (typeof createImageBitmap !== 'function') return Promise.reject(new Error('this browser cannot convert images'));
    return createImageBitmap(blob).then(function (bmp) {
      var c = document.createElement('canvas');
      c.width = bmp.width; c.height = bmp.height;
      var g = c.getContext('2d');
      if (mime === 'image/jpeg') { g.fillStyle = '#fff'; g.fillRect(0, 0, c.width, c.height); }
      g.drawImage(bmp, 0, 0);
      return new Promise(function (resolve, reject) {
        c.toBlob(function (out) {
          if (out && out.type === mime) resolve(out);
          else reject(new Error('this browser cannot save ' + (mime.split('/')[1] || mime).toUpperCase()));
        }, mime, 0.92);
      });
    });
  }

  function download(images) {
    var set = state.set;
    var positions = selectedPositions();
    if (!set || !positions.length || state.busy) return;
    var keepAlpha = set.kind === 'logos';
    var zipOut = set.zip || zipName(set.base);
    state.busy = true;
    var done = 0;
    var f = FORMATS[state.format];
    updateBar('Downloading 0 of ' + positions.length + '…');

    var fetches = positions.map(function (pos) {
      var plan = fetchPlan(images[pos].src, state.format, keepAlpha);
      return fetch(plan.url, { mode: 'cors', credentials: 'omit', headers: { Accept: plan.accept } })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.blob();
        })
        .then(function (blob) { return toFormat(blob, f.mime); })
        .then(function (blob) {
          done++; updateBar('Downloading ' + done + ' of ' + positions.length + '…');
          return { ok: true, pos: pos, name: images[pos].fileBase ? images[pos].fileBase + '.' + f.ext : zipFileName(set.base, pos, f.ext), input: blob };
        })
        .catch(function (e) { return { ok: false, pos: pos, why: e && e.message }; });
    });

    Promise.all([loadZip(), Promise.all(fetches)]).then(function (res) {
      var lib = res[0], files = res[1];
      var good = files.filter(function (f) { return f.ok; });
      var bad = files.filter(function (f) { return !f.ok; });
      if (!good.length) throw new Error('none of the ' + files.length + ' photos could be downloaded');
      updateBar('Building the ZIP…');
      return lib.downloadZip(good.map(function (f) { return { name: f.name, input: f.input }; })).blob().then(function (blob) {
        saveBlob(blob, zipOut);
        return { good: good, bad: bad };
      });
    }).then(function (r) {
      state.busy = false;
      if (r.bad.length) {
        updateBar(r.bad.length + ' of ' + (r.good.length + r.bad.length) + ' photos could not be downloaded (photo ' +
          r.bad.map(function (f) { return f.pos + 1; }).join(', ') + '). The ZIP holds the other ' + r.good.length + '.', 'warn');
      } else {
        updateBar('Downloaded ' + zipOut + ' (' + r.good.length + ' ' + f.label + (r.good.length === 1 ? '' : 's') + ').', 'ok');
      }
    }).catch(function (e) {
      state.busy = false;
      updateBar('Download failed: ' + (e && e.message ? e.message : 'unknown error') + '. Nothing was saved.', 'error');
    });
  }

  function saveBlob(blob, name) {
    var url = URL.createObjectURL(blob);
    var a = el('a', { href: url, download: name, style: 'display:none' });
    document.body.appendChild(a);
    a.click();
    setTimeout(function () { URL.revokeObjectURL(url); a.remove(); }, 60000);
  }

  // ── Boot ────────────────────────────────────────────────────────────────────────────────────

  var booted = false;
  function boot() {
    if (booted) return;
    root = document.getElementById('content-library-root');
    if (!root || !api()) return;
    var sess = null;
    try { sess = JSON.parse(sessionStorage.getItem('rep_portal_session') || 'null'); } catch (e) { sess = null; }
    if (!sess) return; // still on the sign-in screen; `rep-portal:authenticated` retries
    booted = true;
    window.addEventListener('hashchange', route);
    // the drawer's page config can arrive after the page: redraw the tabs (and leave Create ads if it was switched off)
    window.addEventListener('rep-portal:page-config', function () {
      if (!state.index) return;
      if (state.tab === 'ads' && !canAds()) route(); else renderTabs();
      var ok = canAds();
      [].forEach.call(root.querySelectorAll('[data-ads-btn]'), function (b) { b.hidden = !ok; });
    });
    loadIndex();
  }

  window.ContentLibrary = {
    _pure: {
      adsAllowed: adsAllowed, adsHash: adsHash,
      norm: norm, searchKey: searchKey, searchProducts: searchProducts, filterByChips: filterByChips,
      thumbUrl: thumbUrl, jpgUrl: jpgUrl, zipFileName: zipFileName, zipName: zipName, priceLabel: priceLabel,
      money: money, ageText: ageText, sortProducts: sortProducts, hasDates: hasDates, addedLabel: addedLabel, keyFacts: keyFacts, optionLines: optionLines, splitMedia: splitMedia, parseHash: parseHash,
      sectionType: sectionType, fileKey: fileKey, fullSize: fullSize, pageExtras: pageExtras,
      formatOf: formatOf, fetchPlan: fetchPlan, FORMATS: FORMATS, tileClickAction: tileClickAction,
      isBrandHash: isBrandHash, brandSetFor: brandSetFor, photoTag: photoTag, brandDownloadName: brandDownloadName,
      fileSizeLabel: fileSizeLabel, fileTypeLabel: fileTypeLabel,
      TABS: TABS, parseRoute: parseRoute, articleSitemapPaths: articleSitemapPaths, parseArticleSitemap: parseArticleSitemap,
      blogLabel: blogLabel, sortArticles: sortArticles, searchTitles: searchTitles, logoFileBase: logoFileBase,
      pageImages: pageImages, lowerCaseFileUrl: lowerCaseFileUrl, ARTICLE_PAGE: ARTICLE_PAGE, COLLECTION_PAGE: COLLECTION_PAGE, FACILITY_PAGE: FACILITY_PAGE,
    },
  };

  if (typeof document !== 'undefined' && document.addEventListener) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
    window.addEventListener('rep-portal:authenticated', boot);
  }
})();
/* END CUSTOM_CONTENTLIBRARY_BY_CLAUDE */
